/**
 * Victorian Rent Validation Utilities
 *
 * Validates that rent does not exceed advertised price for Victorian properties,
 * as required by Victorian rental regulations.
 */

import { VIC, VICTORIA } from 'app/utils/address/helpers'

/**
 * Property interface with address and state information
 * Note: Property objects in the codebase have inconsistent structure:
 * - Some have state directly on property (property.state)
 * - Some have state nested under address (property.address.state)
 */
export interface Property {
  state?: string // Direct state property (used in some components)
  address?: {
    state?: string // Nested state property (used in other components)
  }
}

/**
 * Offer interface with weekly rent information
 */
export interface Offer {
  weeklyRent?: number
}

/**
 * Checks if a property is located in Victoria
 *
 * @param property - Property object with state or address.state
 * @returns True if the property is in Victoria (VIC)
 */
export const isVictorianProperty = (property?: Property | null): boolean => {
  // Check both possible locations for state property
  // Some components use property.state, others use property.address.state
  const state = property?.state || property?.address?.state
  // Handle both "VIC" and "Victoria" formats (case-insensitive)
  return state?.toUpperCase() === VIC || state?.toUpperCase() === VICTORIA
}

/**
 * Validates that proposed rent does not exceed advertised price for Victorian properties
 *
 * This validation enforces Victorian rental regulations that prohibit offering
 * properties above their advertised rent price.
 *
 * @param proposedRent - The rent being proposed/entered by the user
 * @param property - Property object with address.state
 * @param offer - Property offer object with weeklyRent (advertised price)
 * @returns Error message if validation fails, null if valid
 *
 * @example
 * // Victorian property with advertised rent $450
 * validateVictorianRent(500, { address: { state: 'VIC' }}, { weeklyRent: 450 })
 * // Returns: "Rent cannot exceed $450"
 *
 * validateVictorianRent(400, { address: { state: 'VIC' }}, { weeklyRent: 450 })
 * // Returns: null (valid)
 *
 * // Non-Victorian property allows any rent
 * validateVictorianRent(500, { address: { state: 'NSW' }}, { weeklyRent: 450 })
 * // Returns: null (validation skipped)
 */
export const validateVictorianRent = (
  proposedRent: number | string | null | undefined,
  property?: Property | null,
  offer?: Offer | null,
): string | null => {
  // Skip validation if not a Victorian property
  if (!isVictorianProperty(property)) {
    return null
  }

  // Skip validation if no advertised rent available
  if (!offer?.weeklyRent) {
    return null
  }

  // Parse proposed rent to number (handles both string and number input)
  const proposed =
    typeof proposedRent === 'string'
      ? parseFloat(proposedRent.trim())
      : proposedRent

  // Skip validation if proposed rent is not a valid number
  if (!proposed || isNaN(proposed)) {
    return null
  }

  // Check if proposed rent exceeds advertised rent
  if (proposed > offer.weeklyRent) {
    return `Rent cannot exceed $${offer.weeklyRent}`
  }

  // Validation passed
  return null
}
